import { DataProvider, HttpError } from 'react-admin';

import { requirePartner } from '@src/providers/dataProvider/shared';

import { fetchConditions } from './fetchConditions';
import type { ConditionOption } from './types';

interface ConditionsDataProvider {
  getList: () => Promise<{ data: ConditionOption[], total: number }>;
  getOne: () => Promise<never>;
  getMany: () => Promise<never>;
  getManyReference: () => Promise<never>;
  create: () => Promise<never>;
  update: () => Promise<never>;
  updateMany: () => Promise<never>;
  delete: () => Promise<never>;
  deleteMany: () => Promise<never>;
}

/**
 * Data provider for conditions resource.
 *
 * Conditions are data-only (no UI) and provide valid condition values for pricing rules.
 * getList returns all condition options for the current partner.
 */
const conditionsDataProviderImpl: ConditionsDataProvider = {
  getList: async () => {
    try {
      const partner = requirePartner();
      const data = await fetchConditions({ partnerId: partner.id });

      return {
        data,
        total: data.length,
      };
    } catch (error) {
      if (error instanceof HttpError) throw error;
      console.error('conditionsDataProvider.getList failed:', {
        error: error instanceof Error ? error.message : error,
      });
      const status = (error as { status?: number }).status || 500;
      throw new HttpError('Failed to load conditions.', status);
    }
  },

  getOne: async () => {
    throw new Error('getOne not implemented for conditions resource');
  },

  getMany: async () => {
    throw new Error('getMany not implemented for conditions resource');
  },

  getManyReference: async () => {
    throw new Error('getManyReference not implemented for conditions resource');
  },

  create: async () => {
    throw new Error('create not implemented for conditions resource');
  },

  update: async () => {
    throw new Error('update not implemented for conditions resource');
  },

  updateMany: async () => {
    throw new Error('updateMany not implemented for conditions resource');
  },

  delete: async () => {
    throw new Error('delete not implemented for conditions resource');
  },

  deleteMany: async () => {
    throw new Error('deleteMany not implemented for conditions resource');
  },
};

// Cast to DataProvider for react-admin compatibility.
// React-admin's DataProvider methods are generic, which prevents type-safe
// single-resource providers. We define proper types above and cast here.
export const conditionsDataProvider = conditionsDataProviderImpl as unknown as DataProvider;

export default conditionsDataProvider;
